import React, { useEffect, useState } from "react";
import "../../styles/scss/salaryStatements.scss";
import { useLoginCheck } from "../../hooks/useLoginCheck";

const apiUrl = process.env.REACT_APP_API_URL;

const formatInstructorName = (i) => {
  if (i.first_name && i.last_name) {
    return `${i.username}（${i.first_name} ${i.last_name}）`;
  }
  return i.username;
};

const defaultYearMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1); // 通常は前月分を扱うことが多い
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function SalaryStatementsPage() {
  useLoginCheck();

  const [yearMonth, setYearMonth] = useState(defaultYearMonth());
  const [items, setItems] = useState([]); // [{instructor, has_statement, signed_url, uploaded_at, ...}]
  const [files, setFiles] = useState({}); // {instructor_id: File}
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resultMsg, setResultMsg] = useState(null);

  // 月の一覧取得
  const fetchList = () => {
    if (!yearMonth) return;
    setLoading(true);
    setResultMsg(null);
    fetch(`${apiUrl}/api/salary/statements/list/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ year_month: yearMonth }),
    })
      .then((r) => r.json())
      .then((data) => {
        setItems(data.items || []);
        setFiles({});
      })
      .catch((e) => {
        console.error("一覧取得エラー:", e);
        setResultMsg({ type: "error", text: `一覧取得に失敗: ${e.message}` });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearMonth]);

  const handleFileChange = (instructorId, file) => {
    setFiles((prev) => {
      const next = { ...prev };
      if (file) next[instructorId] = file;
      else delete next[instructorId];
      return next;
    });
  };

  const handleUpload = async () => {
    const entries = Object.entries(files);
    if (entries.length === 0) {
      alert("アップロード対象のファイルがありません");
      return;
    }
    setUploading(true);
    setResultMsg(null);
    try {
      const fd = new FormData();
      fd.append("year_month", yearMonth);
      entries.forEach(([id, f]) => fd.append(`file_${id}`, f));

      const res = await fetch(`${apiUrl}/api/salary/statements/upload/`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `status ${res.status}`);

      const okCount = (data.results || []).length;
      const errCount = (data.errors || []).length;
      setResultMsg({
        type: errCount > 0 ? "error" : "success",
        text: `成功: ${okCount} 件 / 失敗: ${errCount} 件${
          errCount > 0
            ? ` — ${data.errors
                .map((e) => `[${e.instructor_id || e.key}] ${e.error}`)
                .join(", ")}`
            : ""
        }`,
      });
      fetchList();
    } catch (e) {
      setResultMsg({ type: "error", text: `アップロード失敗: ${e.message}` });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (instructorId, name) => {
    if (
      !window.confirm(
        `${name} の ${yearMonth} 給与明細を削除しますか？（S3 のPDFも削除されます）`
      )
    )
      return;
    try {
      const res = await fetch(`${apiUrl}/api/salary/statements/delete/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          instructor_id: instructorId,
          year_month: yearMonth,
        }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      fetchList();
    } catch (e) {
      alert(`削除失敗: ${e.message}`);
    }
  };

  const selectedCount = Object.keys(files).length;

  return (
    <div className="management-salary-statements">
      <h1>給与明細アップロード</h1>

      <div className="toolbar">
        <div>
          <label>対象月</label>
          <input
            type="month"
            value={yearMonth}
            onChange={(e) => setYearMonth(e.target.value)}
          />
        </div>
        <button onClick={fetchList} disabled={loading}>
          {loading ? "読込中..." : "再読込"}
        </button>
        <span className="hint">
          ※ 画像（JPEG/PNG/HEIC等）を選ぶと PDF に変換して S3 にアップロードします
        </span>
      </div>

      {loading && <div>読込中...</div>}

      {!loading && (
        <table className="statements">
          <thead>
            <tr>
              <th style={{ width: 60 }}>状態</th>
              <th>講師</th>
              <th>現在の明細</th>
              <th>アップロード日時</th>
              <th>新ファイル選択</th>
              <th style={{ width: 70 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const i = it.instructor;
              const name = formatInstructorName(i);
              const selectedFile = files[i.id];
              return (
                <tr
                  key={i.id}
                  className={it.has_statement ? "has-statement" : ""}
                >
                  <td>
                    {it.has_statement ? (
                      <span className="badge uploaded">済</span>
                    ) : (
                      <span className="badge pending">未</span>
                    )}
                  </td>
                  <td>
                    {name}
                  </td>
                  <td>
                    {it.signed_url ? (
                      <a
                        className="preview"
                        href={it.signed_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        PDFを開く
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="muted">
                    {it.updated_at
                      ? new Date(it.updated_at).toLocaleString("ja-JP")
                      : "—"}
                  </td>
                  <td>
                    <input
                      type="file"
                      className="file-input"
                      accept="application/pdf,image/*,.heic,.heif"
                      onChange={(e) =>
                        handleFileChange(i.id, e.target.files?.[0] || null)
                      }
                    />
                    {selectedFile && (
                      <span className="selected-file">
                        ✓ {selectedFile.name}
                      </span>
                    )}
                  </td>
                  <td>
                    {it.has_statement && (
                      <button
                        className="delete"
                        onClick={() => handleDelete(i.id, name)}
                      >
                        削除
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="footer">
        <button
          onClick={handleUpload}
          disabled={uploading || selectedCount === 0}
        >
          {uploading
            ? "アップロード中..."
            : `アップロード（${selectedCount} 件）`}
        </button>
      </div>

      {resultMsg && (
        <div className={`upload-result ${resultMsg.type}`}>{resultMsg.text}</div>
      )}
    </div>
  );
}

export default SalaryStatementsPage;
